if (!customElements.get('product-carousel')) {
/**
 * <product-carousel> — a CSS scroll-snap track with previous/next buttons.
 * Buttons scroll by the number of fully visible items, fade out at the
 * edges (is-idle) and respect reduced motion. The track itself remains a
 * normal horizontally scrollable region without JavaScript.
 */
class ProductCarousel extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-carousel-track]');
    this.prev = this.querySelector('[data-carousel-prev]');
    this.next = this.querySelector('[data-carousel-next]');
    if (!this.track) return;

    this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.prev?.addEventListener('click', () => this.scrollByPage(-1));
    this.next?.addEventListener('click', () => this.scrollByPage(1));
    this.track.addEventListener('scroll', () => this.queueUpdate(), { passive: true });
    this.track.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.scrollByPage(1);
      } else if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.scrollByPage(-1);
      }
    });
    if ('ResizeObserver' in window) new ResizeObserver(() => this.update()).observe(this.track);
    this.update();
  }

  /* Reference: scroll three quarters of the visible track, rounded down to
   * whole items so the next card lands on the left edge. */
  get step() {
    const first = this.track.firstElementChild;
    if (!first) return this.track.clientWidth;
    const gap = parseFloat(getComputedStyle(this.track).columnGap || getComputedStyle(this.track).gap) || 0;
    const width = first.getBoundingClientRect().width + gap;
    const items = Math.max(1, Math.floor((this.track.clientWidth * 0.75 + gap) / width));
    return width * items;
  }

  /* In RTL the track scrolls into negative scrollLeft, so "next" moves the
   * other way. */
  get rtl() {
    return getComputedStyle(this.track).direction === 'rtl';
  }

  scrollByPage(direction) {
    const sign = this.rtl ? -1 : 1;
    this.track.scrollBy({ left: direction * this.step * sign, behavior: this.reduceMotion.matches ? 'auto' : 'smooth' });
  }

  queueUpdate() {
    if (this.ticking) return;
    this.ticking = true;
    window.requestAnimationFrame(() => {
      this.update();
      this.ticking = false;
    });
  }

  update() {
    const { scrollWidth, clientWidth } = this.track;
    const scrollLeft = Math.abs(this.track.scrollLeft);
    const atStart = scrollLeft <= 2;
    const atEnd = scrollLeft + clientWidth >= scrollWidth - 2;
    const scrollable = scrollWidth > clientWidth + 2;
    this.prev?.classList.toggle('is-idle', atStart || !scrollable);
    this.next?.classList.toggle('is-idle', atEnd || !scrollable);
    this.prev?.setAttribute('aria-disabled', String(atStart || !scrollable));
    this.next?.setAttribute('aria-disabled', String(atEnd || !scrollable));
  }
}

if (!customElements.get('product-carousel')) customElements.define('product-carousel', ProductCarousel);
}
